import { useCallback, useSyncExternalStore } from 'react';

/**
 * Media queries, readable from React.
 *
 * Most of the responsive work in this app is CSS, and should stay that way.
 * These exist for the parts CSS genuinely cannot express: the phone layout is a
 * different component tree (a bottom sheet holding the panels, rather than
 * three panels floating over the map), and touch devices need a tap-to-inspect
 * card because they never fire the hover the tooltip is built on.
 *
 * The query strings below are duplicated verbatim in styles/app.css. They have
 * to agree -- if one moves, move both.
 */
function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      const mql = window.matchMedia(query);
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    },
    [query],
  );

  return useSyncExternalStore(
    subscribe,
    () => window.matchMedia(query).matches,
    () => false,
  );
}

/**
 * The phone layout.
 *
 * Width alone is not enough. A phone held sideways is 844x390 -- wider than
 * most tablets are tall -- and that is the viewport the old layout failed on
 * hardest, stacking a 214px-tall stats panel on top of the masthead and pushing
 * "Clear selection" off the bottom edge. The height clause catches it. It is
 * qualified by `pointer: coarse` so a deliberately short desktop window keeps
 * the pointer-precise layout it can still use.
 */
export const PHONE_QUERY = '(max-width: 719px), (max-height: 559px) and (pointer: coarse)';

/** Between the phone sheet and the full three-panel desktop layout. */
export const TABLET_QUERY = '(min-width: 720px) and (max-width: 1079px)';

/**
 * No hover available. Distinct from `isPhone`: an iPad runs the tablet layout
 * but still cannot hover, and a touchscreen laptop can do both.
 */
export const TOUCH_QUERY = '(hover: none) and (pointer: coarse)';

export const useIsPhone = () => useMediaQuery(PHONE_QUERY);
export const useIsTablet = () => useMediaQuery(TABLET_QUERY);
export const useIsTouch = () => useMediaQuery(TOUCH_QUERY);
